/**
 * Control-KPI hooks (P280) -- define KPIs (auto from the twin / manual),
 * enter manual readings, and derive Test of Effectiveness from them.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { api, type ApiError } from "shared/api/client";
import type {
  ControlKpi,
  ControlKpiCreateIn,
  ControlKpiList,
  KpiReadingRow,
  KpiSampleResult,
  TestEffectivenessResult,
} from "shared/api/types";

export const kpiKeys = {
  list: (controlId: string) => ["control-kpis", controlId] as const,
  readings: (kpiId: string) => ["kpi-readings", kpiId] as const,
};

export function useControlKpis(
  controlId: string,
): UseQueryResult<ControlKpiList, ApiError> {
  return useQuery({
    queryKey: kpiKeys.list(controlId),
    queryFn: () => api.get<ControlKpiList>(`/v1/framework/controls/${controlId}/kpis`),
    enabled: Boolean(controlId),
  });
}

/** P282.3: a KPI's reading history (newest-first), for the trend sparkline. */
export function useKpiReadings(
  kpiId: string,
  enabled: boolean,
): UseQueryResult<KpiReadingRow[], ApiError> {
  return useQuery({
    queryKey: kpiKeys.readings(kpiId),
    queryFn: () => api.get<KpiReadingRow[]>(`/v1/framework/kpis/${kpiId}/readings`),
    enabled: enabled && Boolean(kpiId),
  });
}

export function useCreateKpi(
  controlId: string,
): UseMutationResult<ControlKpi, ApiError, ControlKpiCreateIn> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      api.post<ControlKpi>(`/v1/framework/controls/${controlId}/kpis`, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: kpiKeys.list(controlId) }),
  });
}

export function useAddKpiReading(
  controlId: string,
): UseMutationResult<KpiReadingRow, ApiError, { kpiId: string; value: number }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ kpiId, value }) =>
      api.post<KpiReadingRow>(`/v1/framework/kpis/${kpiId}/readings`, { value }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: kpiKeys.list(controlId) }),
  });
}

export function useDeleteKpi(
  controlId: string,
): UseMutationResult<void, ApiError, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (kpiId) => api.delete<void>(`/v1/framework/kpis/${kpiId}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: kpiKeys.list(controlId) }),
  });
}

export function useDeriveToE(
  cycleId: string,
  controlId: string,
): UseMutationResult<TestEffectivenessResult, ApiError, void> {
  const qc = useQueryClient();
  return useMutation({
    // E1 (round 8) -- this mutation's caller awaits `mutateAsync` and
    // catches, which react-query cannot see. Without this the global
    // failure sink announces the server's detail as well, so one
    // failure reaches the reader twice in two wordings.
    meta: { handled: true },
    mutationFn: () =>
      api.post<TestEffectivenessResult>(
        `/v1/framework/cycles/${cycleId}/controls/${controlId}/test-effectiveness`,
        {},
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["control-tests", cycleId, controlId] });
      void qc.invalidateQueries({ queryKey: ["framework-assessments"] });
    },
  });
}

/**
 * Sample every auto KPI on the estate now (round 11 D5).
 *
 * ADMIN ONLY, and deliberately not wrapped in anything that hides
 * that: `POST /v1/admin/twin/kpi/sample` is `require_admin`, so the
 * verb is offered to the seat that can complete it and nobody else.
 * A preparer meets a sentence instead -- offering an action to a seat
 * that cannot finish it is the repeat defect this programme spent four
 * rounds closing.
 *
 * Estate-wide rather than per-KPI, because that is what the endpoint
 * does: the same pass the scheduler runs on cadence. The panel says so
 * rather than implying one row was refreshed.
 */
export function useSampleKpis(
  controlId: string,
): UseMutationResult<KpiSampleResult, ApiError, void> {
  const qc = useQueryClient();
  return useMutation({
    /* The caller passes its own `onError` to `mutate`, which says "I
       will handle this" -- and react-query cannot see it: a per-call
       callback is stored on the observer, not in `mutation.options`,
       so the global failure sink fires as well. MEASURED, not assumed:
       a probe with a per-call onError and no marker raised the sink's
       toast on its own, which is one failure reaching the reader twice
       in two wordings -- the round 8 E1 defect, from a door the
       ratchet was not watching. */
    meta: { handled: true },
    mutationFn: () => api.post<KpiSampleResult>("/v1/admin/twin/kpi/sample", {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: kpiKeys.list(controlId) });
    },
  });
}
